import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MeetingListItem, ProjectActivityResponse, ProjectDetail, ProjectMeetingsResponse, ProjectShareDto, ProjectSlackResponse, SharedMeetingDto, SharedTodoDto } from '../../shared/api-types.js';
import { SLACK_FINDING_IMPORTANCE_LABELS, SLACK_FINDING_KIND_NAMES, TODO_STATUSES, TODO_STATUS_LABELS } from '../../shared/enums.js';
import { PATHS } from '../paths.js';
import {
  Button,
  Chip,
  DataTable,
  EM_DASH,
  FailureView,
  Icon,
  LinkButton,
  ProjectChip,
  RelativeTime,
  StateView,
  StatusPill,
  browserTimeZone,
  formatCount,
  formatList,
  listSeparators,
  formatDate,
  formatDateOnly,
  formatDateTime,
  formatTime,
  pluralise,
  timeZoneLabel,
  type DataTableColumn,
  type DataTableStatus,
} from '../ui/index.js';
import { describeProjectActivity, ownActsExtent, PROJECT_ACTIVITY_GRAIN, projectActivityExtent, sharedActivityWords } from './lib/activity.js';
import { slackTodayOffText } from './lib/assistant.js';
import { parseMarkdownLite } from './lib/markdown.js';
import {
  INACTIVE_SHARE_SENTENCE,
  SLACK_NEVER_SHARED_SENTENCE,
  SLACK_NEVER_SHARED_TITLE,
  SLACK_TODAY_WITHHELD_SENTENCE,
  shareMetaWords,
  shareRevocable,
  shareStands,
  shareStatePresentation,
  sharedMeetingsCaption,
  sharedTodosCaption,
  sharerShareSentence,
  type SharedGroup,
} from './lib/projects.js';
import type { LazyRequestState } from './lib/use-lazy-request.js';
import { ActivityFeed, ActivitySkeleton, MarkdownBlocks } from './meeting-tabs.js';
import './pages.css';

// The project record page's related collections (design §7.3 R3; 06-modules §5.3): every embedded table is a DataTable
// with its sort in the URL under its own namespace, a declared default, type-aware client sorting over a complete set
// (the shared endpoints answer every row, so no total line is owed) and a caption naming whose rows they are; each chooses
// its mobile strategy (cards here — a row has more than four fields and none is comparative). What crosses on a share is
// S1's list and nothing else, read-only: the recipient reads and files alongside, never edits the sharer's rows (S2).

const STATUS_RANK = new Map<string, number>(TODO_STATUSES.map((status, index) => [status, index]));

/**
 * The dash for a value not recorded (04-data §4: never a blank), with its explanation spoken. Why `role="img"`: ARIA
 * forbids a name on a bare span (role generic) and the desktop screen readers ignore one there, so a reader heard the
 * dash and never the reason; a named graphic is announced as its label.
 */
export function EmptyDash({ label }: { label: string }) {
  return <span className="muted" role="img" aria-label={label}>{EM_DASH}</span>;
}

// --- Shared to-dos (S1: text, status, due date, responsible) ------------------------------------------------------------

export interface SharedTodosTableProps {
  group: SharedGroup;
  /** The sharer's project name per share id (`ProjectDetail.mergedInShares`), for the caption; the shared page passes its own. */
  projectNames: ReadonlyMap<string, string>;
}

export function SharedTodosTable({ group, projectNames }: SharedTodosTableProps) {
  const caption = sharedTodosCaption(group, projectNames);
  const columns = useMemo<DataTableColumn<SharedTodoDto>[]>(() => [
    { key: 'text', header: 'To-do', type: 'text', sortValue: (todo) => todo.text, card: 'title', cell: (todo) => <span className="project-shared__text">{todo.text}</span> },
    {
      key: 'status',
      header: 'Status',
      type: 'enum',
      sortValue: (todo) => STATUS_RANK.get(todo.status) ?? 99,
      card: 'meta',
      cell: (todo) => <StatusPill tone={todo.status === 'done' ? 'success' : todo.status === 'dismissed' ? 'neutral' : 'info'} icon={todo.status === 'open' ? 'circle' : undefined}>{TODO_STATUS_LABELS[todo.status]}</StatusPill>,
    },
    {
      key: 'due_date',
      header: 'Due',
      type: 'date',
      sortValue: (todo) => todo.dueDate,
      card: 'field',
      cell: (todo) => (todo.dueDate ? <time dateTime={todo.dueDate} className="num">{formatDateOnly(todo.dueDate)}</time> : <EmptyDash label="No due date" />),
    },
    { key: 'responsible', header: 'Responsible', type: 'text', sortValue: (todo) => todo.responsible, card: 'field', cell: (todo) => todo.responsible ?? <EmptyDash label="Nobody responsible" /> },
  ], []);
  return (
    <DataTable<SharedTodoDto, string>
      caption={caption}
      failureNoun="the shared to-dos"
      captionVisible
      columns={columns}
      rows={group.items}
      rowKey={(todo) => todo.id}
      sort={{ defaultSort: { key: 'due_date', dir: 'asc' }, namespace: `shared_todos_${group.shareId}` }}
      clientSort
      empty={{ title: 'No to-dos here', description: `${group.sharedBy.displayName} has no to-dos in this project that can cross on a share.` }}
      mobile="cards"      className="project-shared-table"
    />
  );
}

// --- Shared meetings (S1: title, date, attendee names, summary, key points, decisions) ----------------------------------

export interface SharedMeetingsTableProps {
  group: ProjectMeetingsResponse['shared'][number];
}

/** The words of a shared meeting's disclosure trigger; the meeting's title names the row for assistive technology. */
export const SHARED_MEETING_DISCLOSURE_LABEL = 'Summary, key points and decisions';

/**
 * A shared meeting's summary, key points and decisions behind a disclosure, closed by default. Why closed (04-data
 * §1.12): expansion is for a bounded supplement read while comparing rows, and a whole summary is not one — open on
 * every row it buried the comparative columns under a page of prose each, and on a phone made every card a document.
 * A shared meeting has no page of its own for the recipient (S2: its id answers 404 on their routes), so the detail
 * lives inline, as §1.12's one disclosure within the row or card. The native `<details>` is the client's disclosure
 * idiom (meeting-tabs.tsx, the record's own Details), and exposes its expanded state to assistive technology.
 */
function SharedMeetingDetail({ meeting }: { meeting: SharedMeetingDto }) {
  const blocks = useMemo(() => parseMarkdownLite(meeting.summaryMarkdown), [meeting.summaryMarkdown]);
  return (
    <details className="disclosure project-shared__detail">
      <summary>
        <Icon name="chevron-down" size={16} />
        <span>{SHARED_MEETING_DISCLOSURE_LABEL}<span className="visually-hidden"> of {meeting.title}</span></span>
      </summary>
      <div className="disclosure__body stack stack--2">
        {meeting.attendeeNames.length > 0 ? <p className="text-sm secondary">{`With ${formatList(meeting.attendeeNames)}`}</p> : null}
        {blocks.length > 0 ? <div className="summary-body"><MarkdownBlocks blocks={blocks} /></div> : <p className="muted text-sm">No summary was shared for this meeting.</p>}
        {meeting.keyPoints.length > 0 ? (
          <div className="stack stack--1">
            <p className="text-sm"><strong>Key points</strong></p>
            <ul className="summary-list">{meeting.keyPoints.map((point, index) => <li key={index}>{point}</li>)}</ul>
          </div>
        ) : null}
        {meeting.decisions.length > 0 ? (
          <div className="stack stack--1">
            <p className="text-sm"><strong>Decisions</strong></p>
            <ul className="summary-list">{meeting.decisions.map((decision, index) => <li key={index}>{decision}</li>)}</ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}

export function SharedMeetingsTable({ group }: SharedMeetingsTableProps) {
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);
  const columns = useMemo<DataTableColumn<SharedMeetingDto>[]>(() => [
    { key: 'title', header: 'Title', type: 'text', sortValue: (meeting) => meeting.title, card: 'title', cell: (meeting) => meeting.title },
    {
      key: 'started_at',
      header: `When (${zoneLabel})`,
      name: 'When',
      type: 'date',
      sortValue: (meeting) => meeting.startedAt,
      card: 'meta',
      cell: (meeting) => (meeting.startedAt
        ? <time dateTime={meeting.startedAt} className="num" title={formatDateTime(meeting.startedAt, { timeZone })}>{formatDate(meeting.startedAt, { timeZone })}, {formatTime(meeting.startedAt, { timeZone })}</time>
        : <EmptyDash label="Start time not recorded" />),
    },
    { key: 'key_points', header: 'Key points', type: 'number', numeric: true, sortValue: (meeting) => meeting.keyPoints.length, card: 'field', cell: (meeting) => formatCount(meeting.keyPoints.length) },
    { key: 'decisions', header: 'Decisions', type: 'number', numeric: true, sortValue: (meeting) => meeting.decisions.length, card: 'field', cell: (meeting) => formatCount(meeting.decisions.length) },
  ], [timeZone, zoneLabel]);
  return (
    <DataTable<SharedMeetingDto, string>
      caption={sharedMeetingsCaption(group)}
      failureNoun="the shared meetings"
      captionVisible
      columns={columns}
      rows={group.items}
      rowKey={(meeting) => meeting.id}
      sort={{ defaultSort: { key: 'started_at', dir: 'desc' }, namespace: `shared_meetings_${group.shareId}` }}
      clientSort
      empty={{ title: 'No meetings here', description: `${group.sharedBy.displayName} has no meetings in this project yet.` }}
      rowDetail={(meeting) => <SharedMeetingDetail meeting={meeting} />}
      detailFrom={0}
      mobile="cards"      className="project-shared-table"
    />
  );
}

// --- The person's own meetings of the project (the Meetings page's rows, server-sorted) ----------------------------------

export const PROJECT_MEETINGS_SORT_KEYS = ['title', 'started_at', 'todo_count', 'attendee_count'] as const;
export type ProjectMeetingsSortKey = (typeof PROJECT_MEETINGS_SORT_KEYS)[number];

export interface OwnMeetingsTableProps {
  rows: readonly MeetingListItem[];
  status: DataTableStatus;
  /** How many the project holds; the table shows them all, so it states the figure only in its caption line. */
  total: number | null;
}

export function OwnMeetingsTable({ rows, status, total }: OwnMeetingsTableProps) {
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);
  const columns = useMemo<DataTableColumn<MeetingListItem>[]>(() => [
    { key: 'title', header: 'Title', type: 'text', sortValue: (meeting) => meeting.title, card: 'title', cell: (meeting) => <Link to={PATHS.meeting(meeting.id)} className="hit-area">{meeting.title}</Link> },
    {
      key: 'started_at',
      header: `When (${zoneLabel})`,
      name: 'When',
      type: 'date',
      sortValue: (meeting) => meeting.startedAt,
      card: 'meta',
      cell: (meeting) => (meeting.startedAt
        ? <time dateTime={meeting.startedAt} className="num" title={formatDateTime(meeting.startedAt, { timeZone })}>{formatDate(meeting.startedAt, { timeZone })}, {formatTime(meeting.startedAt, { timeZone })}</time>
        : <EmptyDash label="Start time not recorded" />),
    },
    // Why the possessive: `openTodoCount` is the meeting's total across every project it is filed under, not the share
    // of it that sits in this project — the header says whose figure it is so the two are never confused.
    { key: 'todo_count', header: 'Meeting’s open to-dos', type: 'number', numeric: true, sortValue: (meeting) => meeting.openTodoCount, card: 'field', cell: (meeting) => formatCount(meeting.openTodoCount) },
    { key: 'attendee_count', header: 'Attendees', type: 'number', numeric: true, sortValue: (meeting) => meeting.attendeeCount, card: 'field', cell: (meeting) => formatCount(meeting.attendeeCount) },
  ], [timeZone, zoneLabel]);
  return (
    <DataTable<MeetingListItem, ProjectMeetingsSortKey>
      caption="Meetings in this project"
      failureNoun="this project’s meetings"
      columns={columns}
      rows={rows}
      rowKey={(meeting) => meeting.id}
      status={status}
      sort={{ defaultSort: { key: 'started_at', dir: 'desc' }, namespace: 'meetings' }}
      clientSort      empty={{
        title: 'No meetings in this project yet',
        description: 'Calis files a meeting here when its summary discusses this project; you can also file one from its page.',
        action: <LinkButton to={PATHS.meetings} variant="secondary">Go to meetings</LinkButton>,
      }}
      mobile="cards"
      footer={total !== null && total > 0 ? <p className="text-sm secondary num">{pluralise(total, { one: '# meeting', other: '# meetings' })} · the whole set. Times are shown in {zoneLabel}.</p> : null}
    />
  );
}

// --- The Slack tab (S4: each item under the rule of its home surface) -----------------------------------------------------

export interface ProjectSlackTabProps {
  request: LazyRequestState<ProjectSlackResponse>;
  /**
   * Whether this installation's daily look at Slack runs (`/api/me` `integrations.slackSweep !== 'off'`). Why the client
   * asks: the route folds "the daily look is off here" into `withheld.matters` beside a refused gate, and the two are
   * different answers (06-modules §5.7) — a fully granted reader must not be told they may not see Slack material
   * because an operator has not switched the sweep on. The Assistant draws the same line for its own Slack today tab.
   */
  sweepOn: boolean;
}

export function ProjectSlackTab({ request, sweepOn }: ProjectSlackTabProps) {
  if (request.status === 'idle' || request.status === 'loading') {
    return <StateView kind="loading" label="Loading Slack findings" minHeight="12rem" />;
  }
  if (request.status === 'error' && request.error) {
    return <FailureView failure={request.error} what="this project’s Slack findings" onRetry={request.reload} minHeight="10rem" />;
  }
  const data = request.data;
  if (!data) return null;
  const nothing = data.findings.length === 0 && data.matters.length === 0;
  return (
    <div className="stack stack--5">
      <section className="stack stack--3" aria-labelledby="project-slack-findings">
        <h3 id="project-slack-findings" className="section-heading">Findings</h3>
        {data.findings.length === 0 ? (
          <p className="text-sm secondary">No Slack finding is filed under this project. A finding is filed when a meeting’s Slack check names this project, or when you file one from its meeting’s Slack tab.</p>
        ) : (
          <ol className="project-slack-list" role="list" aria-label="Slack findings filed under this project">
            {data.findings.map((entry) => (
              <li key={`${entry.meetingId}:${entry.finding.id}`} className="project-slack-card">
                <div className="cluster project-slack-card__head">
                  <Chip tone="neutral">{SLACK_FINDING_KIND_NAMES[entry.finding.kind]}</Chip>
                  <span className="text-xs muted">Importance {SLACK_FINDING_IMPORTANCE_LABELS[entry.finding.importance].toLowerCase()}</span>
                </div>
                <p className="project-slack-card__title"><strong>{entry.finding.title}</strong></p>
                <p className="text-sm">{entry.finding.detail}</p>
                <p className="text-sm secondary">From the Slack check of <Link to={PATHS.meeting(entry.meetingId)}>{entry.meetingTitle}</Link></p>
                <div className="cluster project-slack-card__actions">
                  <LinkButton to={entry.href} size="sm" variant="secondary">Why?</LinkButton>
                  <span className="text-xs muted">Open in Slack from the meeting’s Slack tab.</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
      <section className="stack stack--3" aria-labelledby="project-slack-matters">
        <h3 id="project-slack-matters" className="section-heading">Slack today matters</h3>
        {!sweepOn ? (
          <StateView kind="not-configured" compact title={slackTodayOffText('installation').title} description={slackTodayOffText('installation').description} />
        ) : data.withheld.matters ? (
          <StateView kind="denied" compact title="Slack today is withheld" description={SLACK_TODAY_WITHHELD_SENTENCE} />
        ) : data.matters.length === 0 ? (
          <p className="text-sm secondary">No matter from your Slack day is filed under this project.</p>
        ) : (
          <ol className="project-slack-list" role="list" aria-label="Slack today matters filed under this project">
            {data.matters.map((entry) => (
              <li key={entry.matter.id} className="project-slack-card">
                <div className="cluster project-slack-card__head">
                  <Chip tone="neutral">Matter</Chip>
                  <span className="text-xs muted">Importance {SLACK_FINDING_IMPORTANCE_LABELS[entry.matter.importance].toLowerCase()}</span>
                  {entry.matter.isNew ? <StatusPill tone="info">New today</StatusPill> : null}
                </div>
                <p className="project-slack-card__title"><strong>{entry.matter.title}</strong></p>
                <p className="text-sm">{entry.matter.detail}</p>
                {entry.matter.meetings.length > 0 ? (
                  <p className="text-sm secondary">
                    Concerns {entry.matter.meetings.map((meeting, index, all) => (
                      <span key={meeting.id}>{listSeparators(all.length)[index]}<Link to={PATHS.meeting(meeting.id)}>{meeting.title}</Link></span>
                    ))}
                  </p>
                ) : null}
                <div className="cluster project-slack-card__actions">
                  <LinkButton to={entry.href} size="sm" variant="secondary">Why?</LinkButton>
                  <span className="text-xs muted">Open in Slack from the Assistant’s Slack today tab.</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
      {/* Why this wording: a filing is a membership row keyed to the finding's fingerprint or the matter's id, purged with the
          check or sweep it labels (CLAUDE.md, purgeSlackContextData / purgeSlackSweeps); the project row copies nothing of Slack. */}
      {nothing && (!sweepOn || !data.withheld.matters) ? <p className="text-xs muted">A filing is a label on the check or daily look that found the item, and goes when that record goes; nothing of Slack is copied into the project.</p> : null}
    </div>
  );
}

/** The Slack tab of a project shared with the reader: nothing of Slack crosses (S1), and the panel says so rather than standing empty. */
export function SharedSlackNote() {
  return <StateView kind="empty" icon="lock" title={SLACK_NEVER_SHARED_TITLE} description={SLACK_NEVER_SHARED_SENTENCE} minHeight="10rem" />;
}

// --- The People tab: the sharer's shares table (own page) ----------------------------------------------------------------

export interface SharesTableProps {
  project: ProjectDetail;
  /** The share an act is in flight on; its controls disable meanwhile. */
  busyShareId: string | null;
  onStopSharing: (share: ProjectShareDto) => void;
  onMergeBack: (share: ProjectShareDto) => void;
  onUnmergeBack: (share: ProjectShareDto) => void;
  /** Opens the Share dialog (the collection's own "create", 06-modules §5.6). */
  onShare: () => void;
}

export function SharesTable({ project, busyShareId, onStopSharing, onMergeBack, onUnmergeBack, onShare }: SharesTableProps) {
  const columns = useMemo<DataTableColumn<ProjectShareDto>[]>(() => [
    { key: 'recipient', header: 'Shared with', type: 'text', sortValue: (share) => share.recipient.displayName, card: 'title', cell: (share) => share.recipient.displayName },
    {
      key: 'state',
      header: 'State',
      type: 'enum',
      sortValue: (share) => (shareStands(share.state) ? 0 : share.state === 'inactive' ? 1 : 2),
      card: 'meta',
      cell: (share) => {
        const presentation = shareStatePresentation(share.state);
        const meta = shareMetaWords(share);
        return (
          <span className="project-share-state">
            <StatusPill tone={presentation.tone} icon={presentation.icon}>{presentation.label}</StatusPill>
            {meta.length > 0 ? <span className="text-xs secondary project-share-state__meta">{meta.join(' · ')}</span> : null}
          </span>
        );
      },
    },
    { key: 'since', header: 'Since', type: 'date', sortValue: (share) => share.createdAt, card: 'field', cell: (share) => <RelativeTime value={share.createdAt} mode="both" /> },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      name: 'Actions',
      sortable: false,
      card: 'actions',
      cell: (share) => {
        const busy = busyShareId === share.id;
        // Why Stop sharing outlives the standing states: an inactive share resumes crossing when its recipient is
        // readmitted, and only the sharer can end it (S7) — so it is offered on every share not yet revoked or left.
        // Merge back and Unmerge back wait for a standing one (the server answers 409 COLLEAGUE_INACTIVE otherwise).
        if (!shareRevocable(share.state)) return <span className="text-xs muted">No further action</span>;
        const stands = shareStands(share.state);
        return (
          <span className="cluster">
            {stands && share.mergedIn && !share.mergedBack ? <Button size="sm" variant="secondary" onClick={() => onMergeBack(share)} loading={busy}>Merge back</Button> : null}
            {stands && share.mergedBack ? <Button size="sm" variant="secondary" onClick={() => onUnmergeBack(share)} loading={busy}>Unmerge back</Button> : null}
            <Button size="sm" variant="danger-outline" onClick={() => onStopSharing(share)} disabled={busy}>Stop sharing</Button>
          </span>
        );
      },
    },
  ], [busyShareId, onMergeBack, onStopSharing, onUnmergeBack]);

  return (
    <div className="stack stack--3 project-people">
      <div className="split">
        <p className="text-sm secondary">
          {project.shares.length === 0
            ? 'Nobody sees this project but you.'
            : pluralise(project.shares.filter((share) => shareStands(share.state)).length, { one: '# colleague sees this project', other: '# colleagues see this project', zero: 'No colleague sees this project now' })}
        </p>
        <Button variant="secondary" icon="users" size="sm" onClick={onShare}>Share with…</Button>
      </div>
      <DataTable<ProjectShareDto, string>
        caption="People this project is shared with"
        failureNoun="the people this project is shared with"
        columns={columns}
        rows={project.shares}
        rowKey={(share) => share.id}
        sort={{ defaultSort: { key: 'since', dir: 'desc' }, namespace: 'people' }}
        clientSort
        rowDetail={(share) => {
          const sentence = sharerShareSentence(share);
          return sentence ? <p className="text-sm secondary project-shared-note">{sentence}</p> : null;
        }}
        detailFrom={1}
        empty={{
          title: 'Not shared with anyone',
          description: 'Share it with a colleague by name: they see its meetings and to-dos read live from your account, never copied.',
          action: <Button variant="secondary" icon="users" onClick={onShare}>Share with…</Button>,
        }}
        mobile="cards"
      />
    </div>
  );
}

// --- The People tab on a project shared with the reader ------------------------------------------------------------------

export interface SharedByPanelProps {
  project: ProjectDetail;
  busy: boolean;
  onMergeIn: () => void;
  onUnmergeIn: () => void;
  onLeave: () => void;
}

export function SharedByPanel({ project, busy, onMergeIn, onUnmergeIn, onLeave }: SharedByPanelProps) {
  const share = project.share;
  if (!share) return null;
  const inactive = share.state === 'inactive';
  const presentation = shareStatePresentation(share.state);
  let grouping: ReactNode;
  // Why "until they have access again" (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere):
  // the state is said in the same words as the withheld name above it; it read "until they use it again".
  if (inactive) grouping = <p className="text-sm secondary">{INACTIVE_SHARE_SENTENCE}. Nothing crosses until they have access again; you can still leave.</p>;
  else if (share.mergedInto) {
    grouping = (
      <p className="text-sm secondary project-shared-note">
        Grouped under your project <ProjectChip project={share.mergedInto} />
        {share.mergedBack ? <> — and {share.sharedBy.displayName} groups that project’s own meetings and to-dos with theirs in return, as you agreed when merging in.</> : '.'}
      </p>
    );
  } else grouping = <p className="text-sm secondary">Not grouped with any project of yours. Merge it into one to read its meetings and to-dos beside your own there.</p>;
  return (
    <div className="stack stack--3 project-people">
      <dl className="field-list">
        <dt>Shared by</dt>
        <dd>{share.sharedBy.displayName}</dd>
        <dt>State</dt>
        <dd><StatusPill tone={presentation.tone} icon={presentation.icon}>{presentation.label}</StatusPill></dd>
        <dt>Since</dt>
        <dd><RelativeTime value={project.createdAt} mode="both" /></dd>
      </dl>
      {grouping}
      {!inactive ? (
        <div className="cluster">
          {share.mergedInto === null ? <Button variant="primary" onClick={onMergeIn} disabled={busy}>Merge into one of mine</Button> : null}
          {share.mergedInto !== null ? <Button variant="secondary" onClick={onUnmergeIn} loading={busy}>Unmerge</Button> : null}
        </div>
      ) : null}
      {/* Why "their" for a withheld sharer (the same ruling): the stated withholding is never made possessive. */}
      <p className="text-xs muted">{share.sharedBy.withheld === true ? 'You read their' : `You read ${share.sharedBy.displayName}’s`} meetings and to-dos live and never edit them; only they can end the share.</p>
      {/* Why on its own row, after the note: Leave is irreversible for the reader (only the sharer can share again),
          and a destructive control is never adjacent to the primary (06-modules §5.6, §5.10; 03-components §2). */}
      <div className="project-people__leave">
        <Button variant="danger-outline" size="sm" onClick={onLeave} disabled={busy}>Leave this shared project</Button>
      </div>
    </div>
  );
}

// --- R4: the Activity tab -------------------------------------------------------------------------------------------

/**
 * A project's Activity tab (06-modules §5 R4; 04-data §6), as the route says it is (`view` — the server's decision, never
 * the page's own copy of whether the project is shared):
 * - `record` — the owner's: the project's history, with what it holds stated (the grain) and how much of it this is
 *   (`projectActivityExtent`: from its creation, from the retention window, or from when Calis began recording its own
 *   creations — the Calis conformity re-audit's D31).
 * - `own_acts` and `withheld` — a recipient's (the owner's ruling D32 of 2026-10-10, "History shown as withheld"; 06-modules
 *   §5.7): the project's history is denied in place, said so in the region, never an empty feed under the region's name;
 *   then the reader's own acts on the share, under a heading that says they are theirs, with an empty state of their own.
 * Loading draws skeleton entries at the entry's height (R24), the same for every view, since the view is the answer's.
 */
export function ProjectActivityTab({ request, project, shared, sharerName }: { request: LazyRequestState<ProjectActivityResponse>; project: { id: string; createdAt: string }; shared: boolean; sharerName: string | null }) {
  // Why the page's own `shared` here and nowhere else: what the loading region reserves is layout, decided before the
  // answer; what the region then shows is the answer's `view`, never this flag.
  if (request.status === 'idle' || request.status === 'loading') return <ActivitySkeleton grain={shared ? undefined : PROJECT_ACTIVITY_GRAIN} />;
  if (request.status === 'error' && request.error) return <FailureView failure={request.error} what="this project’s activity" onRetry={request.reload} minHeight="10rem" />;
  const data = request.data;
  if (!data) return null;
  if (data.view === 'record') {
    return (
      <ActivityFeed
        data={data}
        // Why this project's id: an Undo of a merge into it is listed here though written against the project it brought
        // back, and is said from this side (`describeProjectActivity`).
        describe={(event) => describeProjectActivity(event, project.id)}
        subject="this project"
        extent={projectActivityExtent(project, new Date(), browserTimeZone())}
        grain={PROJECT_ACTIVITY_GRAIN}
      />
    );
  }
  const words = sharedActivityWords(sharerName, data.view);
  return (
    <div className="shared-activity">
      <StateView kind="denied" title={words.deniedTitle} description={words.deniedDescription} minHeight="8rem" />
      <section className="shared-activity__own" aria-labelledby="shared-activity-own">
        <h3 id="shared-activity-own" className="shared-activity__heading">{words.ownHeading}</h3>
        <ActivityFeed data={data} describe={describeProjectActivity} subject="your part in this shared project" extent={ownActsExtent(data.view, sharerName)} dayHeadingLevel={4} />
      </section>
    </div>
  );
}
